import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { Progress } from './progress';
import { AlertTriangle, Clock, LogOut } from 'lucide-react';
import { useAuth } from '../../hooks/use-auth';
import { inactivityManager } from '../../lib/inactivity-manager';

interface AutoLogoutDialogProps {
  isOpen: boolean;
  onExtend: () => void;
  onLogout: () => void;
  warningTime: number; // Time in milliseconds until logout
}

export function AutoLogoutDialog({ isOpen, onExtend, onLogout, warningTime }: AutoLogoutDialogProps) {
  const [remainingTime, setRemainingTime] = useState(warningTime);
  const [progress, setProgress] = useState(100);
  const { logoutMutation } = useAuth();
  
  // Update countdown every second
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      const newTime = inactivityManager.getWarningTime();
      setRemainingTime(newTime);
      
      // Calculate progress percentage (5 minutes = 300,000ms)
      const totalWarningTime = 5 * 60 * 1000; // 5 minutes
      const progressPercent = (newTime / totalWarningTime) * 100;
      setProgress(Math.max(0, progressPercent));
      
      // Auto-logout when time reaches 0
      if (newTime <= 0) {
        handleLogout();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen]);
  
  const handleExtend = () => {
    inactivityManager.extendSession();
    onExtend();
  };
  
  const handleLogout = () => {
    inactivityManager.forceLogout();
    logoutMutation.mutate();
    onLogout();
  };
  
  const formatTime = (milliseconds: number): string => {
    const seconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-800">
                Session Timeout Warning
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                You've been inactive for 60 minutes. Your session will expire soon.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Time remaining:</span>
              <span className="font-medium text-slate-800 flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {formatTime(remainingTime)}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
          
          {/* Warning message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-amber-800 font-medium">
                  Your session will automatically end in {formatTime(remainingTime)}.
                </p>
                <p className="text-amber-700 mt-1">
                  Click 'Stay Logged In' to extend your session for another 60 minutes, or 'Logout Now' to end it immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-3">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full sm:w-auto border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout Now
          </Button>
          <Button
            onClick={handleExtend}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
